import {priority} from '@priority-software/priority-proto-api/lib';
import {action, observable, makeObservable, computed, toJS} from 'mobx';
import {ChartType, Pages, bubbleConfigInfo, initialChartInfo} from '../consts';
import {
    ColumnsFieldsNames,
    IBubbleConfig,
    IChartConfig,
    IReport,
    ReportColumn,
    ReportData,
    SerializedReportParams,
    ReportInputMetaData,
    ChartHeader
} from '../types';
import {Report} from './ReportStore';
import {SortType, filterAndSort} from '../componenets/ReportList/utils';
import {getReportData} from '../services/service';
import {COL_IDS} from '../componenets/ReportList/consts';
import texts from '../texts';
import {MultiSeriesChartTypesOptions, SingleSeriesChartTypesOptions} from '../chartConsts';
import {Action, Namespace, getStorageKey} from '../../../services/proto/ServerStorage/utils';
import {getServerStoreData} from '../../../services/proto/ServerStorage/service';
import {prepareExpressionsToStore} from '../utils';
import {getOuterJoinMapFromServer} from '../services/reports';

export class ReportGeneratorStore {
    @observable isOpen: boolean = false;
    @observable isFieldsDialogOpen: boolean = false;
    @observable reports: IReport[];
    @observable forms: Map<string, priority.netitems.nrest.ITreeNode> = new Map();
    @observable currentPage: Pages = Pages.main;
    @observable prevPosition: Pages = Pages.main;
    @observable currentType: string = ChartType.report;
    @observable currentNavPage: number = 1;
    @observable currentReport: Report;
    @observable selectedForm: any;
    @observable listSearchQuery: string;
    @observable chartInfo: IChartConfig;
    @observable chartIsReady: boolean = false;
    @observable sort: SortType;
    @observable currentChartTab: string = '0';
    @observable reportParams: SerializedReportParams[];
    @observable chartData: ReportData;
    @observable isPredictiveModel: boolean;
    chartHeader: ChartHeader;

    setChartHeader = (chartHeader: ChartHeader) => {
        this.chartHeader = chartHeader;
    };

    session: string;
    currPlotValues: string[];
    currExpRepTitle: string;
    expReportInputsMetaData: ReportInputMetaData[];
    selectedFormName?: string;

    constructor(session: string) {
        makeObservable(this);
        this.session = session;
        this.sort = {id: COL_IDS.title, isDesc: false, isDefault: true};
        this.chartInfo = {
            ...initialChartInfo,
            baseForm: this.selectedForm
        };
        this.reportParams = null;
        this.chartData = null;
        this.currPlotValues = null;
        this.currExpRepTitle = null;
        this.expReportInputsMetaData = null;
        this.isPredictiveModel = false;
    }

    @computed
    get isBubble() {
        return this.chartInfo.type === SingleSeriesChartTypesOptions.bubble;
    }

    @computed
    get isKpi() {
        return this.chartInfo.type === SingleSeriesChartTypesOptions.kpi;
    }

    @computed
    get isGauge() {
        return this.chartInfo.type === SingleSeriesChartTypesOptions.angulargauge;
    }

    @computed
    get isMultiSeries() {
        return (Object.values(MultiSeriesChartTypesOptions) as string[]).includes(
            this.chartInfo.type
        );
    }

    @computed
    get getChartData() {
        return toJS(this.chartData);
    }

    @action
    resetChartInfo = () => {
        this.chartInfo = initialChartInfo;
    };

    resetXAxisInfo = () => {
        this.chartInfo.xAxis = null;
        if (this.chartInfo.bubbleConfig?.minX) {
            this.chartInfo.bubbleConfig.minX = null;
        }
        if (this.chartInfo.bubbleConfig?.maxX) {
            this.chartInfo.bubbleConfig.maxX = null;
        }
        this.setChartReady(false);
    };

    resetYAxisInfo = () => {
        this.chartInfo.yAxis = null;
        if (this.chartInfo.bubbleConfig?.minY) {
            this.chartInfo.bubbleConfig.minY = null;
        }
        if (this.chartInfo.bubbleConfig?.maxY) {
            this.chartInfo.bubbleConfig.maxY = null;
        }

        this.setChartReady(false);
    };

    resetZAxisInfo = () => {
        this.chartInfo.bubbleConfig.zBubbleSize = null;
        this.setChartReady(false);
    };

    resetBubbleCategory = () => {
        this.chartInfo.bubbleConfig.category = null;
        this.setChartReady(false);
    };

    resetExplantoryReport = () => {
        this.chartInfo.explanatoryReport = null;
    };

    resetSeriesDimension = () => {
        this.chartInfo.series = null;
        this.setChartReady(false);
    };

    resetChartDimensions = () => {
        this.resetXAxisInfo();
        this.resetYAxisInfo();
        this.resetExplantoryReport();
        this.chartInfo.topX = null;
        if (this.chartInfo.bubbleConfig) {
            this.chartInfo.bubbleConfig = null;
        }
        if (this.chartInfo.series) {
            this.resetSeriesDimension();
        }
        if (this.chartInfo.scales) {
            this.chartInfo.scales = null;
        }
        this.setChartReady(false);
    };

    @action
    setReports = (reports: IReport[]) => {
        if (window.location.search.includes('_shortReportList=1')) {
            this.reports = filterAndSort('', {isDesc: true, id: COL_IDS.updateTime}, reports).slice(
                0,
                300
            );
            this.setSort({isDesc: true, id: COL_IDS.updateTime});
        } else {
            this.reports = reports;
        }
    };

    @action
    setChartData = (data: ReportData) => {
        this.chartData = data;
    };

    @action
    setForms = (forms: Map<string, priority.netitems.nrest.ITreeNode>) => {
        this.forms = forms;
    };

    @action
    open = () => {
        this.isOpen = true;
    };

    @action
    setChartReady = (value: boolean) => {
        this.chartIsReady = value;
    };

    @action
    close = () => {
        this.isOpen = false;
    };

    @action
    setIsFieldsDialogOpen = () => {
        this.isFieldsDialogOpen = !this.isFieldsDialogOpen;
    };

    @action
    setCurrentPage = (page: Pages) => {
        this.currentPage = page;
    };

    @action
    setCurrentChartTab = (tab: string) => {
        this.currentChartTab = tab;
    };

    @action
    setPrevPosition = (page: Pages) => {
        this.prevPosition = page;
    };

    @action
    setChartInfo = (config: IChartConfig) => {
        this.chartInfo = {
            ...this.chartInfo,
            ...config
        };
    };

    onSaveChartInfo = (key: keyof IChartConfig, value: any) => {
        this.setChartInfo({
            [key]: value
        } as IChartConfig);
    };

    @action
    setCurrentType = (type: ChartType) => {
        this.currentType = type;
    };

    @action
    setSelectedForm = (form: string) => {
        this.selectedForm = form;
    };

    @action
    setSelectedFormNAme = (formName: string) => {
        this.selectedFormName = formName;
    };

    @action
    setCurrentReport = (report: Report) => {
        this.currentReport = report;
    };

    getChartConfig = async (id: string) => {
        let chartConfig;
        const key = getStorageKey({
            nameSpace: Namespace.Analytics,
            action: Action.onChartSave,
            storage: id
        });
        let data;
        data = await getServerStoreData([key], false, false);
        if (Object.keys(data).length === 0) {
            data = await getServerStoreData([key]);
        }

        if (data[key]?.Str) {
            chartConfig = JSON.parse(data[key]?.Str);
            if (
                chartConfig.explanatoryReport &&
                !this?.reports.find(report => report.id === chartConfig.explanatoryReport.ReportId)
            ) {
                chartConfig.explanatoryReport = null;
            }
        }
        return chartConfig;
    };

    @action
    duplicateReport = async (id: number | Long) => {
        this.setChartReady(false);
        const reportData = await getReportData(id);
        const reportToDuplicate = this.reports.find(report => report.id === id);

        const newReport = new Report(reportToDuplicate.nodeId);
        newReport.setReportData({
            ...reportData,
            Columns: [],
            ReportId: 0,
            Title: ''
        });

        newReport.updateColumns(prepareExpressionsToStore(reportData.Columns));
        newReport.setDuplicate(id);
        const expressions = reportData.Columns.filter(column => column.IsExpression);
        const expressionsIds = expressions.map(exp => Number(exp.ColId));
        newReport.setExpressionIds(expressionsIds);
        const mapInServer = await getOuterJoinMapFromServer(id);
        newReport.setOuterJoinMap(mapInServer);
        newReport.updateDirty();
        this.setCurrentReport(newReport);
        if (reportData.ReportType === priority.netitems.nrest.RepType.RepType_Chart) {
            const chartConfig = await this.getChartConfig(id.toString());
            if (chartConfig) {
                chartConfig.chartName = '';
                this.setChartInfo(chartConfig);
            }
            this.setPrevPosition(Pages.chart);
            this.setCurrentPage(Pages.chart);
        } else {
            this.setCurrentPage(Pages.report);
        }
    };

    @action
    setSearchQuery = (query: string) => {
        this.listSearchQuery = query;
        this.setNavPage(1);
    };

    @action
    getPageTitle = (): string => {
        switch (this.currentPage) {
            case Pages.main:
                return texts.myReports;
            case Pages.report:
                return this.isPredictiveModel
                    ? texts.predictiveModelPageTitle
                    : texts.reportPageTitle;
            case Pages.chart:
                return texts.chartPageTitle;
            // case Pages.chartDisplay:
            //     return texts.chartDisplayPageTitle;
            default:
                return '';
        }
    };

    @action
    setSort = (sort: SortType) => {
        this.sort = sort;
        this.setNavPage(1);
    };

    setNavPage = (page: number) => {
        this.currentNavPage = page;
    };

    @action
    setReportParams = (reportParams: SerializedReportParams[]) => {
        this.reportParams = reportParams;
    };

    getReportParams = () => {
        return toJS(this.reportParams);
    };

    @action
    setCurrPlotValues = (xValue: string[]) => {
        this.currPlotValues = xValue;
    };

    getCurrPlotValues = () => {
        return this.currPlotValues;
    };

    @action
    setCurrExpRepTitle = (title: string) => {
        this.currExpRepTitle = title;
    };

    getCurrExpRepTitle = () => {
        return this.currExpRepTitle;
    };

    @action
    setExpReportInputsMetaData = (inputsMetaData: ReportInputMetaData[]) => {
        this.expReportInputsMetaData = inputsMetaData;
    };

    getExpReportInputsMetaData = () => {
        return this.expReportInputsMetaData;
    };

    @action
    setIsPredictiveModel = (value: boolean) => {
        this.isPredictiveModel = value;
    };

    isReportColumn = (obj: any) => {
        const requiredProperties: ColumnsFieldsNames[] = [
            ColumnsFieldsNames.ColId,
            ColumnsFieldsNames.Title,
            ColumnsFieldsNames.NodeId
        ];
        return obj && typeof obj === 'object' && requiredProperties.every(prop => prop in obj);
    };

    cleanupChangedColumns = (chartInfo: IChartConfig | IBubbleConfig, columns: ReportColumn[]) => {
        const title = ColumnsFieldsNames.Title;
        let deletionCount = 0;

        const columnsConfig = Object.keys(chartInfo)
            .filter(key => chartInfo.hasOwnProperty(key) && this.isReportColumn(chartInfo[key]))
            .map(key => key);

        columnsConfig
            .filter(key => !columns.some(column => column[title] === chartInfo[key]?.[title]))
            .forEach(key => {
                delete chartInfo[key];
                deletionCount++;
            });
        return deletionCount;
    };

    checkAxesCompleted = (chartInfo: IChartConfig) => {
        const essentialChartData = !!chartInfo.xAxis && !!chartInfo.yAxis;

        if (this.isMultiSeries) {
            return essentialChartData && !!chartInfo.series;
        }
        if (this.isBubble) {
            const allBubblePropsSet = Object.keys(bubbleConfigInfo).every(
                key => !!chartInfo.bubbleConfig?.[key] && chartInfo.bubbleConfig?.[key] !== '-'
            );
            return essentialChartData && allBubblePropsSet;
        }
        if (this.isKpi) {
            return !!chartInfo.yAxis;
        }
        if (this.isGauge) {
            return !!chartInfo.yAxis && !!chartInfo.scales;
        }
        return essentialChartData;
    };
}
